import { Component, computed, ElementRef, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { Popover } from 'primeng/popover';
import { concat, map, of, switchMap, timer } from 'rxjs';
import { UserFacade } from '@app/store/user';
import { User } from '@app/store/shared/models';

const SEND_COOLDOWN = 60_000;
const POPOVER_BASE_Z_INDEX = 100;

@Component({
    selector: 'app-access-button',
    templateUrl: './access-button.component.html',
    styleUrls: ['./access-button.component.css'],
    imports: [TranslateModule, ButtonModule, MessageModule, Popover],
})
export class AccessButtonComponent {
    private readonly userFacade = inject(UserFacade);
    private readonly elementRef = inject(ElementRef);

    readonly user = input.required<User>();
    readonly emailEdited = input(false);
    readonly testId = input.required<string>();

    readonly popoverVisible = signal(false);
    readonly baseZIndex = POPOVER_BASE_Z_INDEX;

    private readonly triggerWidth = signal(0);
    readonly popoverStyle = computed(() => ({ '--p-popover-trigger-width': `${this.triggerWidth()}px` }));

    readonly actionKey = computed(() => (this.user().activated ? 'users.access.action.passwordReset' : 'users.access.action.activation'));
    readonly descriptionKey = computed(() => (this.user().activated ? 'users.access.confirm.passwordReset' : 'users.access.confirm.activation'));
    readonly sending = computed(() => this.userFacade.accessSendingId() === this.user().id);

    private readonly lastSentAt = computed(() => this.user().lastSentEmailAt);
    readonly cooldown = toSignal(
        toObservable(this.lastSentAt).pipe(
            switchMap((lastSentAt) => {
                const remaining = lastSentAt ? lastSentAt.getTime() + SEND_COOLDOWN - Date.now() : 0;

                return remaining > 0 ? concat(of(true), timer(remaining).pipe(map(() => false))) : of(false);
            }),
        ),
        { initialValue: false },
    );

    onToggle(event: MouseEvent, popover: Popover): void {
        this.triggerWidth.set(this.elementRef.nativeElement.offsetWidth);
        popover.toggle(event);
    }

    onConfirm(popover: Popover): void {
        popover.hide();
        this.userFacade.sendAccountAccessEmail(this.user().id);
    }
}
